import React, { useEffect, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { portalApi } from "../lib/portalApi";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, Download, FileText, PlayCircle, Target } from "lucide-react";

const Inner = () => {
  const [sections, setSections] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [passedSet, setPassedSet] = useState(new Set());
  const [activeId, setActiveId] = useState(null);
  const [score, setScore] = useState("");

  const load = async () => {
    const s = await portalApi.curriculumSections();
    const a = await portalApi.curriculumMyAttempts();
    setSections(s.sections || []);
    setAttempts(a.attempts || []);
    setPassedSet(new Set(a.passed_sections || []));
    if (!activeId && (s.sections || []).length) setActiveId(s.sections[0].id);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const num = Number(score);
    if (Number.isNaN(num) || num < 0 || num > 10) {
      return toast.error("Score must be 0-10");
    }
    try {
      const r = await portalApi.curriculumAttempt({ section_id: activeId, score_out_of_10: num });
      toast[r.passed ? "success" : "warning"](r.message);
      setScore("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Attempt failed"); }
  };

  const download = async () => {
    try {
      const md = await portalApi.curriculumExport();
      const blob = new Blob([md], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "AuditIQ_Certification_Curriculum.md";
      a.click(); URL.revokeObjectURL(url);
      toast.success("Curriculum downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const active = sections.find(s => s.id === activeId);
  const progressPct = sections.length ? Math.round((passedSet.size / sections.length) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="portal-curriculum-learner">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Certification Curriculum</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Learn · Assess · Certify</h1>
          <p className="text-cream-dim text-sm mt-2 max-w-2xl">
            Complete all {sections.length} sections with a minimum assessment score of <span className="text-gold font-semibold">9.2 / 10</span> (≈92%) to earn your AuditIQ certification.
          </p>
        </div>
        <Button onClick={download} className="btn-ghost-gold h-10 px-4 text-xs" data-testid="curriculum-download-btn">
          <Download size={14} className="mr-2" /> Download Full Curriculum
        </Button>
      </div>

      {/* Progress */}
      <div className="glass p-5">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-cream">
            <Target size={14} className="text-electric" />
            <span>Sections passed</span>
            <span className="text-gold font-semibold">{passedSet.size} / {sections.length}</span>
          </div>
          <div className="text-gold font-display text-xl" data-testid="curriculum-progress-pct">{progressPct}%</div>
        </div>
        <div className="mt-3 h-2 w-full bg-navy-deep rounded overflow-hidden border border-gold-dim/20">
          <div className="h-full bg-gradient-to-r from-electric to-gold transition-all"
            style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Section list */}
        <nav className="glass p-3 space-y-1 h-fit" data-testid="curriculum-section-list">
          {sections.map(s => {
            const passed = passedSet.has(s.id);
            const isActive = s.id === activeId;
            return (
              <button key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`w-full text-left flex items-start gap-3 px-3 py-2 rounded transition-colors ${
                  isActive ? "bg-navy-soft border-l-2 border-gold" : "hover:bg-navy-soft/50"
                }`}
                data-testid={`curriculum-section-${s.order}`}
              >
                <div className={`shrink-0 mt-0.5 ${passed ? "text-[color:var(--auditiq-success)]" : "text-steel"}`}>
                  {passed ? <CheckCircle2 size={16} /> : <BookOpen size={16} />}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-steel">Section {s.order}</div>
                  <div className={`text-sm truncate ${isActive ? "text-gold" : "text-cream"}`}>{s.title}</div>
                </div>
              </button>
            );
          })}
        </nav>

        {/* Active section */}
        {active ? (
          <section className="glass p-6 space-y-6" data-testid="curriculum-active-section">
            <header>
              <div className="text-[10px] uppercase tracking-[0.28em] text-electric">
                Section {active.order} of {sections.length}
              </div>
              <h2 className="font-display text-2xl mt-1">{active.title}</h2>
            </header>

            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-steel mb-2">Lessons</div>
              <ul className="space-y-2">
                {(active.lessons || []).map((l, i) => (
                  <li key={i} className="flex items-start gap-3 text-cream text-sm">
                    <PlayCircle size={14} className="text-electric mt-0.5 shrink-0" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-gold-dim/15 pt-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-steel mb-2">
                <FileText size={12} /> Assessment
              </div>
              <div className="text-cream text-sm">
                <span className="text-gold">{active.assessment}</span>
                <span className="text-cream-dim"> · {active.questions} questions · minimum pass score </span>
                <span className="text-gold font-semibold">{active.pass_score}/10</span>
              </div>

              <div className="mt-4 flex gap-3 items-center flex-wrap">
                <Input
                  type="number" min="0" max="10" step="0.1"
                  value={score}
                  onChange={e => setScore(e.target.value)}
                  placeholder="Enter your score (0–10)"
                  className="bg-navy-deep border-gold-dim/40 max-w-xs"
                  data-testid="curriculum-score-input"
                />
                <Button onClick={submit} disabled={!score} className="btn-gold h-10 px-5 text-xs"
                  data-testid="curriculum-submit-attempt">
                  Submit Attempt
                </Button>
                {passedSet.has(active.id) && (
                  <span className="text-[color:var(--auditiq-success)] text-xs flex items-center gap-1">
                    <CheckCircle2 size={14} /> Passed
                  </span>
                )}
              </div>
            </div>

            {/* History */}
            <div className="border-t border-gold-dim/15 pt-5">
              <div className="text-[11px] uppercase tracking-[0.2em] text-steel mb-2">My Recent Attempts</div>
              <div className="space-y-1">
                {attempts.filter(a => a.section_id === active.id).slice(0, 5).map(a => (
                  <div key={a.id} className="text-xs flex items-center gap-3" data-testid={`attempt-${a.id}`}>
                    <span className={a.passed ? "text-[color:var(--auditiq-success)]" : "text-[color:var(--auditiq-critical)]"}>
                      {a.passed ? "PASS" : "FAIL"}
                    </span>
                    <span className="text-gold font-mono">{a.score_out_of_10}/10</span>
                    <span className="text-cream-dim">{new Date(a.attempted_at).toLocaleString()}</span>
                  </div>
                ))}
                {!attempts.filter(a => a.section_id === active.id).length && (
                  <div className="text-cream-dim text-xs italic">No attempts yet.</div>
                )}
              </div>
            </div>
          </section>
        ) : (
          <div className="glass p-10 text-center text-cream-dim">Loading curriculum…</div>
        )}
      </div>
    </div>
  );
};

const PortalCurriculumLearner = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalCurriculumLearner;
